import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

interface IAxiosInstance extends AxiosInstance {
  setToken: (token: string | null) => void;
}

const PROD_URL: string = import.meta.env.VITE_API_PROD_URL;
const DEV_URL: string = import.meta.env.VITE_API_DEV_URL;

export const baseURL: string =
  import.meta.env.NODE_ENV === "development" ? DEV_URL : PROD_URL;

const axiosInstance: IAxiosInstance = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 30000,
}) as IAxiosInstance;

axiosInstance.setToken = (token: string | null): void => {
  if (token) {
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common["Authorization"];
  }
};

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    if (import.meta.env.MODE === "development") {
      console.log(`[Request] ${config.method?.toUpperCase()} to ${config.url}`);
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response.data?.data !== undefined
      ? response.data.data
      : response.data;
  },
  (error: AxiosError<any>) => {
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      console.error(`[API Error] Status ${status}:`, data);
      switch (status) {
        case 401:
          console.warn("Unauthorized access detected. Token may be expired.");
          break;
        case 404:
          console.error("API Endpoint not found.");
          break;
      }
      return Promise.reject(data || error.response);
    }
    return Promise.reject(error);
  }
);

const setAuthToken = (token: string) => {
  localStorage.setItem("token", token);
  axiosInstance.setToken(token);
};

const deleteAuthToken = () => {
  axiosInstance.setToken(null);
  localStorage.removeItem("token");
};

export { setAuthToken, deleteAuthToken };
export default axiosInstance;
